import type { LoginResponse, RefreshResponse, SessionUser } from '@axsolutionsdev/shared';
import type { AxiosInstance } from 'axios';

/**
 * BE 의 login/refresh 응답 사용자 형상.
 *
 * `SessionUser` 가 정식 이름이다 (BE↔FE 공유, `@axsolutionsdev/shared`, I.1).
 * `LoginUser` 는 본 모듈의 종전 이름 → BC 를 위해 동일 타입의 alias 로 둔다.
 */
export type LoginUser = SessionUser;
export type { SessionUser, LoginResponse, RefreshResponse } from '@axsolutionsdev/shared';

export interface LoginRequest {
  username: string;
  password: string;
  /**
   * 멀티테넌시 resolver 가 도입되기 전(G.x)까지는 명시 필수.
   * docs/04 §3.1 동작 1.
   */
  tenantCode: string;
}

export async function loginRequest(
  client: AxiosInstance,
  body: LoginRequest,
): Promise<LoginResponse> {
  const { data } = await client.post<LoginResponse>('/auth/login', body);
  return data;
}

/**
 * BE `POST /api/auth/refresh` — HttpOnly 쿠키 기반.
 * 본 호출은 인터셉터의 401-handling 루프에 들어가지 않도록 별도 구성으로 호출한다.
 * (호출처: stores/auth.ts refresh(), api/client.ts 401 핸들러)
 */
export async function refreshRequest(client: AxiosInstance): Promise<RefreshResponse> {
  const { data } = await client.post<RefreshResponse>('/auth/refresh', null, {
    // refresh 자체의 401 은 인터셉터가 logout 으로 해석하면 안 됨 → 별도 마크.
    // (client.ts 의 인터셉터에서 config.url + 본 헤더로 식별)
    headers: { 'X-Skip-Auth-Refresh': '1' },
  });
  return data;
}

/** BE `POST /api/auth/logout` — refresh token revoke + 쿠키 clear. */
export async function logoutRequest(client: AxiosInstance): Promise<void> {
  await client.post('/auth/logout', null, {
    // 로그아웃 호출의 401 도 추가 처리 불필요 (이미 로그아웃 의도).
    headers: { 'X-Skip-Auth-Refresh': '1' },
  });
}
